import { createDispatchFunction } from 'shared/helpers';
import { partial, orderBy } from 'lodash';
import MaterialChainService from './MaterialChainService';

// Actions
const GET_ROOM_MATERIALS = 'materialChain/GET_ROOM_MATERIALS';
const RESET_ROOM_MATERIALS = 'materialChain/RESET_ROOM_MATERIALS';
const SET_ROOM_MATERIALS = 'materialChain/SET_ROOM_MATERIALS';
const GET_TC_EVENT_MATERIAL_TYPES = 'materialChain/GET_TC_EVENT_MATERIAL_TYPES';
const GET_ROOM_TYPE_MATERIAL_TYPES = 'materialChain/GET_ROOM_TYPE_MATERIAL_TYPES';
const GET_EVENT_ALL_MATERIALS = 'materialChain/GET_EVENT_ALL_MATERIALS';
const GET_EVENT_MATERIALS = 'materialChain/GET_EVENT_MATERIALS';
const SET_EVENT_MATERIALS = 'materialChain/SET_EVENT_MATERIALS';
const GET_MATERIAL_TYPES_BY_ROOM_TYPES = 'materialChain/GET_MATERIAL_TYPES_BY_ROOM_TYPES';
const GET_ROOM_MATERIALS_COUNT = 'materialChain/GET_ROOM_MATERIALS_COUNT';

const initialState = {
  loading: false,
  roomMaterials: [],
  errorRoomMaterials: {},
  tcEventMaterialTypes: [],
  roomTypeMaterialTypes: [],
  eventAllMaterials: [],
  eventMaterials: [],
  materialTypesByRoomTypes: [],
  roomMaterialsCount: [],
  loadingRoomMaterialTypes: false,
};

// Reducer
export default function materialChainReducer(state = initialState, action = {}) {
  switch (action.type) {
    case GET_ROOM_MATERIALS:
      return {
        ...state,
        roomMaterials: action.result.data || [],
      };
    case RESET_ROOM_MATERIALS:
      return {
        ...state,
        roomMaterials: [],
      };
    case GET_ROOM_TYPE_MATERIAL_TYPES:
      return {
        ...state,
        roomTypeMaterialTypes: action.result.data || [],
        loadingRoomMaterialTypes: action.result.loading || state.loadingRoomMaterialTypes,
      };
    case GET_MATERIAL_TYPES_BY_ROOM_TYPES: {
      let materialsList = [];
      if (action.result.data) {
        const roomTypes = action.result.payload;
        roomTypes.forEach((roomType, idx) => {
          materialsList[roomType.id] = action.result.data[idx];
        });
      }
      return {
        ...state,
        materialTypesByRoomTypes: materialsList || [],
      };
    }
    case GET_TC_EVENT_MATERIAL_TYPES:
      return {
        ...state,
        tcEventMaterialTypes: action.result.data || [],
      };

    case SET_ROOM_MATERIALS:
      return {
        ...state,
        roomMaterials:
          (action.result.error || (action.result.loading && state.roomMaterials)
            ? state.roomMaterials
            : action.result.data) || [],
        errorRoomMaterials: action.result.error || {},
      };
    case GET_EVENT_ALL_MATERIALS:
      return {
        ...state,
        eventAllMaterials: action.result.data || [],
        loading: action.result.loading,
      };
    case GET_EVENT_MATERIALS:
      return {
        ...state,
        eventMaterials: action.result.data || [],
        loading: action.result.loading,
      };
    case SET_EVENT_MATERIALS:
      return {
        ...state,
        eventMaterials:
          (action.result.error || (action.result.loading && state.eventMaterials)
            ? state.eventMaterials
            : action.result.data) || [],
        errorEventMaterials: action.result.error || {},
      };
    case GET_ROOM_MATERIALS_COUNT:
      return {
        ...state,
        roomMaterialsCount: (action.result.data && action.result.data.rooms) || [],
        loading: action.result.loading,
      };
    default:
      return state;
  }
}

// Action Creators

export function getRoomMaterialsAction(result) {
  return { type: GET_ROOM_MATERIALS, result };
}
export function resetRoomMaterialsAction(result) {
  return { type: RESET_ROOM_MATERIALS, result };
}

export function setRoomMaterialsAction(result) {
  return { type: SET_ROOM_MATERIALS, result };
}

export function getTCEventMaterialTypesAction(result) {
  return { type: GET_TC_EVENT_MATERIAL_TYPES, result };
}

export function getRoomTypeMaterialTypesAction(result) {
  return { type: GET_ROOM_TYPE_MATERIAL_TYPES, result };
}

export function getEventAllMaterialsAction(result) {
  return { type: GET_EVENT_ALL_MATERIALS, result };
}

export function getEventMaterialsAction(result) {
  return { type: GET_EVENT_MATERIALS, result };
}

export function setEventMaterialsAction(result) {
  return { type: SET_EVENT_MATERIALS, result };
}

export function clearMaterialTypesByRoomTypesAction() {
  return { type: GET_MATERIAL_TYPES_BY_ROOM_TYPES, result: {} };
}

export function getMaterialTypesByRoomTypesAction(result) {
  return { type: GET_MATERIAL_TYPES_BY_ROOM_TYPES, result };
}
export function getRoomMaterialsCountAction(result) {
  return { type: GET_ROOM_MATERIALS_COUNT, result };
}

// Action dispatcher functions

export function getRoomMaterials(roomId) {
  return createDispatchFunction(
    partial(MaterialChainService.getRoomMaterials, roomId),
    getRoomMaterialsAction
  );
}
export function resetRoomMaterials() {
  return createDispatchFunction(
    () =>
      new Promise((resolve) => {
        resolve();
      }),
    resetRoomMaterialsAction
  );
}

export function setRoomMaterials(roomId, data) {
  return createDispatchFunction(
    partial(MaterialChainService.setRoomMaterials, roomId, data),
    setRoomMaterialsAction
  );
}

export function getTCEventMaterialTypes(tcEventId) {
  return createDispatchFunction(
    partial(MaterialChainService.getTCEventMaterialTypes, tcEventId),
    getTCEventMaterialTypesAction
  );
}

export function getRoomTypeMaterialTypes(roomTypeId) {
  return createDispatchFunction(
    partial(MaterialChainService.getRoomTypeMaterialTypes, roomTypeId),
    getRoomTypeMaterialTypesAction
  );
}

export function getEventAllMaterials(eventId) {
  return createDispatchFunction(
    partial(MaterialChainService.getEventAllMaterials, eventId),
    getEventAllMaterialsAction
  );
}

export function getEventMaterials(eventId) {
  return createDispatchFunction(
    partial(MaterialChainService.getEventMaterials, eventId),
    getEventMaterialsAction
  );
}

export function setEventMaterials(eventId, data) {
  return createDispatchFunction(
    partial(MaterialChainService.setEventMaterials, eventId, data),
    setEventMaterialsAction
  );
}

export function getMaterialTypesByRoomTypes(roomTypes) {
  const rTypes = orderBy(roomTypes, 'displayOrder');
  return createDispatchFunction(
    partial(MaterialChainService.getMaterialTypesByRoomTypes, rTypes),
    getMaterialTypesByRoomTypesAction,
    rTypes
  );
}

export function getRoomMaterialsCount(eventId) {
  return createDispatchFunction(
    partial(MaterialChainService.getRoomMaterialsCount, eventId),
    getRoomMaterialsCountAction
  );
}
